import { gateWebApp } from './gate';
import { WebAppGenerationError, normalizeAppCode } from './appCode';
import { buildWebAppPrompt } from './prompt';
import type { AppPlan } from './plan/planTypes';

export interface WebAppGenerationIO {
  generate(input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
    structured?: boolean;
  }): Promise<{ content: string }>;
}

export interface WebAppBuildResult {
  /** The app body: markup + optional <style> + one <script>. Not a whole document. */
  appCode: string;
  attempts: number;
  repairRounds: number;
  /**
   * Total failed gate passes across the whole build. Named and counted separately from
   * `repairRounds` because the mini-app path shipped a bug where the two silently
   * disagreed — chat prose said "2 compile fixes" while the log recorded 3.
   */
  gateFailures: number;
}


export const MAX_REPAIR_ROUNDS = 2;


export function buildRepairPrompt(appCode: string, diagnostics: string[]): string {
  return [
    'Your app was rejected by the checker. Fix EVERY problem listed and return the COMPLETE',
    'app body again — same output rules as before (markup + optional <style> + one <script>,',
    'no <html>/<head>/<body>, no fences, no commentary).',
    '',
    'Problems:',
    ...diagnostics.map((d) => `- ${d}`),
    '',
    'This was your app:',
    appCode,
  ].join('\n');
}

export async function generateWebApp(
  goal: string,
  io: WebAppGenerationIO,
  signedIn: boolean,
  vibe?: string | null,
  plan?: AppPlan | null,
): Promise<WebAppBuildResult> {
  const systemPrompt = buildWebAppPrompt(vibe, plan);
  const messages: { role: 'user' | 'assistant'; content: string }[] = [
    { role: 'user', content: `Build this app: ${goal}` },
  ];

  let lastCode = '';
  let lastDiagnostics: string[] = [];
  let gateFailures = 0;

  for (let attempt = 1; attempt <= MAX_REPAIR_ROUNDS + 1; attempt++) {
    // `structured: true` matters: without it the server proxy routes to the cheap chat
    // model at a 1,500-token cap, which is what produced uniformly ~100-line output on the
    // mini-app path and was misread as a taste problem rather than a capacity one.
    const { content } = await io.generate({ systemPrompt, messages, signedIn, structured: true });
    const appCode = normalizeAppCode(content);
    lastCode = appCode;

    const result = gateWebApp(appCode, { mustCollect: !!plan?.collects });
    if (result.ok) {
      return { appCode, attempts: attempt, repairRounds: attempt - 1, gateFailures };
    }

    gateFailures++;
    lastDiagnostics = result.diagnostics;
    messages.push({ role: 'assistant', content: appCode });
    messages.push({ role: 'user', content: buildRepairPrompt(appCode, result.diagnostics) });
  }

  throw new WebAppGenerationError(
    `The app still had problems after ${MAX_REPAIR_ROUNDS} fix attempts.`,
    lastDiagnostics,
    lastCode,
    gateFailures,
  );
}

export { WebAppGenerationError, normalizeAppCode };
